import { type SalesMetricV2 } from "./types";

export function makeSnapchatPaidImpressions(): SalesMetricV2 {
  const id = "snapchatPaidImpressions";
  const name = "Snapchat Paid Impressions";

  return {
    id,
    name,
    fullName: name,
    description:
      "The total number of times your ad was served to a Snapchatter. Tracked when the ad " +
      "fully renders on a device for the first time during a Snapchatter’s viewing session",
    category: "snapchat",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatEarnedImpressions(): SalesMetricV2 {
  const id = "snapchatEarnedImpressions";
  const name = "Snapchat Earned Impressions";

  return {
    id,
    name,
    fullName: name,
    description:
      "The total number of times a Snapchatter created an impression for your ad when " +
      "it was not served to them",
    category: "snapchat",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatTotalImpressions(): SalesMetricV2 {
  const id = "snapchatTotalImpressions";
  const name = "Snapchat Total Impressions";

  return {
    id,
    name,
    fullName: name,
    description:
      "The total number of times your ad was served to a Snapchatter - the total of paid " +
      "and earned impressions",
    category: "snapchat",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatSwipes(): SalesMetricV2 {
  const id = "snapchatSwipes";
  const name = "Snapchat Swipes";

  return {
    id,
    name,
    fullName: name,
    description:
      "The total number of times a Snapchatter has swiped up on attachments within your " +
      "Story Ad (aggregated across all attachments)",
    category: "snapchat",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatSwipesRate(): SalesMetricV2 {
  const id = "snapchatSwipesRate";
  const name = "Snapchat Swipes Rate";

  return {
    id,
    name,
    fullName: name,
    description:
      "The rate at which Snapchatters swiped up on swipeable videos or images within your " +
      "story ad to reveal the attachment below (averaged across all attachments)",
    category: "snapchat",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatThumbstopRate(): SalesMetricV2 {
  const id = "snapchatThumbstopRate";
  const name = "Snapchat Thumbstop Rate";

  return {
    id,
    name,
    fullName: name,
    description: "2 second video views / Total video views",
    category: "snapchat",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatProfileClicks(): SalesMetricV2 {
  const id = "snapchatProfileClicks";
  const name = "Snapchat Profile Clicks";

  return {
    id,
    name,
    fullName: name,
    description:
      "The total number of taps on your Public Profile from your ads",
    category: "snapchat",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatVideoViews(): SalesMetricV2 {
  const id = "snapchatVideoViews";
  const name = "Snapchat Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "The total number of impressions that meet the qualifying video view criteria of at " +
      "least 2 seconds of consecutive watch time or a swipe up action on the Top Snap",
    category: "snapchat",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatVideoViewsTimeBased(): SalesMetricV2 {
  const id = "snapchatVideoViewsTimeBased";
  const name = "Snapchat Video Views Time Based";

  return {
    id,
    name,
    fullName: name,
    description:
      "The total number of impressions that meet the qualifying video view criteria of at " +
      "least 2 seconds, not including swipe ups",
    category: "snapchat",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatVideoViews15S(): SalesMetricV2 {
  const id = "snapchatVideoViews15S";
  const name = "Snapchat Video Views 15s";

  return {
    id,
    name,
    fullName: name,
    description:
      "15 Second Video Views: The total number of impressions that meet the qualifying video " +
      "view criteria of at least 15 seconds, or 97% completion if it's shorter than 15 seconds, " +
      "or a swipe up action on the ad (15 Second Video Views are not supported in the " +
      "Delivery Insight Export)",
    category: "snapchat",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatQuartile1(): SalesMetricV2 {
  const id = "snapchatQuartile1";
  const name = "Snapchat video p25 watched";

  return {
    id,
    name,
    fullName: name,
    description:
      "Video Plays at 25%: Number of times your Top Snap was viewed to 25% or swiped by a " +
      "user. This metric is accredited by the Media Rating Council for some ad formats",
    category: "snapchat",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatQuartile2(): SalesMetricV2 {
  const id = "snapchatQuartile2";
  const name = "Snapchat video p50 watched";

  return {
    id,
    name,
    fullName: name,
    description:
      "Video Plays at 50%: Number of times your Top Snap was viewed to 50% or " +
      "swiped by a user. " +
      "This metric is accredited by the Media Rating Council for some ad formats",
    category: "snapchat",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatQuartile3(): SalesMetricV2 {
  const id = "snapchatQuartile3";
  const name = "Snapchat video p75 watched";

  return {
    id,
    name,
    fullName: name,
    description:
      "Video Plays at 75%: Number of times your Top Snap was viewed to 75% or swiped by a user. " +
      "This metric is accredited by the Media Rating Council for some ad formats",
    category: "snapchat",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatViewCompletion(): SalesMetricV2 {
  const id = "snapchatViewCompletion";
  const name = "Snapchat View Completion";

  return {
    id,
    name,
    fullName: name,
    description:
      "Number of times your Top Snap was viewed to 97% or swiped by a user. This metric is " +
      "accredited by the Media Rating Council for some ad formats",
    category: "snapchat",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatAttachmentTotalViewTimeMillis(): SalesMetricV2 {
  const id = "snapchatAttachmentTotalViewTimeMillis";
  const name = "Snapchat Attachment Total View Time Millis";

  return {
    id,
    name,
    fullName: name,
    description:
      "The total number of seconds spent viewing your attachment across all swipe ups",
    category: "snapchat",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatTransactions(): SalesMetricV2 {
  const id = "snapchatTransactions";
  const name = "Snapchat Transactions";

  return {
    id,
    name,
    fullName: name,
    description: "The total number of purchases made in your app or website",
    category: "snapchat",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatRevenue(): SalesMetricV2 {
  const id = "snapchatRevenue";
  const name = "Snapchat Revenue";

  return {
    id,
    name,
    fullName: name,
    description: "The total value of purchases made in your app or website",
    category: "snapchat",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatROAS(): SalesMetricV2 {
  const id = "snapchatROAS";
  const name = "Snapchat ROAS";

  return {
    id,
    name,
    fullName: name,
    description:
      "Snapchat reported return on ad spend, this is calculated by taking the total revenue " +
      "reported by Snapchat and dividing by spend",
    category: "snapchat",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeSnapchatCAC(): SalesMetricV2 {
  const id = "snapchatCAC";
  const name = "Snapchat CAC";

  return {
    id,
    name,
    fullName: name,
    description: "Spend / transactions",
    category: "snapchat",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
